import { Box, TextField } from "@mui/material";
import { ReadOnlyIsPrivateLedger } from "./ReadOnlyIsPrivateLedger";
import {
  ClientDetails,
  useClientDetails,
} from "../../context/clientDetailsContext";

const timestampFormatter = (timestamp: number | undefined) => {
  if (!timestamp) {
    throw Error("Timestamp is required");
  }
  const date = new Date(timestamp).toLocaleDateString("en-GB");
  const time = new Date(timestamp).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
  return `${date} ${time}`;
};
const hasRequiredFields = (details: ClientDetails): boolean => {
  const requiredFields = [
    "dateCreated",
    "lastUpdated",
    "pricingPlan",
    "products",
  ] as const;

  return requiredFields.every((key) => key in details);
};

function EntityInfoFields() {
  const {
    client: { details },
    clientId,
  } = useClientDetails();

  if (!clientId) {
    throw Error("Client ID is required");
  }
  if (!details) {
    throw Error("Client details are required");
  }

  if (!hasRequiredFields(details)) {
    throw Error("Missing required client details fields");
  }

  const {
    dateCreated,
    lastUpdated,
    pricingPlan,
    products,
    isPrivateLedger = false,
  } = details;

  return (
    <>
      <Box sx={{ paddingRight: 2, paddingBottom: 4 }}>
        <ReadOnlyIsPrivateLedger isPrivate={isPrivateLedger || false} />
      </Box>
      <TextField
        sx={{ paddingBottom: 2 }}
        value={clientId}
        id="client-id"
        label="ID"
        size="small"
      />
      <Box
        data-testid="timestamp-container"
        sx={{
          display: "flex",
          direction: "row",
          justifyContent: "space-between",
        }}
      >
        <TextField
          sx={{ paddingBottom: 2, width: "49%" }}
          value={timestampFormatter(dateCreated)}
          id="created-at-date"
          label="Created At"
          size="small"
        />
        <TextField
          sx={{ paddingBottom: 2, width: "49%" }}
          value={timestampFormatter(lastUpdated)}
          id="last-updated-date"
          label="Last Updated"
          size="small"
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          direction: "row",
          justifyContent: "space-between",
        }}
      >
        <TextField
          sx={{ paddingBottom: 2, width: "49%" }}
          value={pricingPlan?.level.toUpperCase()}
          id="client-pricing-plan"
          label="Pricing Plan"
          size="small"
        />
        <TextField
          sx={{ paddingBottom: 2, width: "49%" }}
          value={
            pricingPlan?.billingInterval
              ? pricingPlan.billingInterval.toUpperCase()
              : "N/A"
          }
          id="client-billing-interval"
          label="Billing Interval"
          size="small"
        />
      </Box>
      <TextField
        sx={{ paddingBottom: 2, width: "100%" }}
        value={products?.length ? products.join(", ") : "No products added"}
        id="client-products"
        label="Products"
        size="small"
      />
    </>
  );
}

export default EntityInfoFields;
